
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import { TixTime } from '@/TixTime/TixTime'
import { fetchSessionsAndPrices, fetchSessionsAvailability } from '@/api/Sessions'
import { CalendarApiResponse, fetchCalendarForTicketTypes } from '@/api/calendar'
import type { EventDetails, LinkedTG } from '@/api/types/processedEntities'
import { processCalendarApiResponse } from '@/calendar/helpers'
import AvailabilityStatus from '@/components/elements/AvailabilityStatus.vue'
import MobileCartButton from '@/components/elements/MobileCartButton.vue'
import MobileTotal from '@/components/elements/MobileTotal.vue'
import RenewMembershipMessage from '@/components/elements/RenewMembershipMessage.vue'
import AdmitDetailsFields from '@/components/events/AdmitDetailsFields.vue'
import DynamicMessageGroup from '@/components/events/DynamicMessageGroup.vue'
import EventDetailErrorMessages from '@/components/events/EventDetailErrorMessages.vue'
import SelectDate from '@/components/events/SelectDate.vue'
import SelectQuantities from '@/components/events/SelectQuantities.vue'
import SelectSession from '@/components/events/SelectSession.vue'
import FormInput2 from '@/components/forms/FormInput2.vue'
import FormValidationGuard from '@/components/forms/FormValidationGuard.vue'
import Survey2 from '@/components/forms/Survey2.vue'
import ValidationError from '@/errors/ValidationError'
import { apiErrorMessageOrRethrow, getApiErrorEntity } from '@/errors/helpers'
import { capacityThreshold, showAvailability } from '@/helpers/AvailabilityHelpers'
import { isChainedUpsell } from '@/helpers/EventHelpers'
import { ShowPricesConfig, sessionsWithPrices, showPrices } from '@/helpers/PriceDisplay'
import { reserveTicketsPayload } from '@/helpers/Reserve'
import { SurveySpecWithTicketGroupID, surveySpecsWithTicketGroupId } from '@/helpers/SurveyHelpers'
import { sumQuantities } from '@/helpers/TicketTypeQuantities'
import { reportFormValidity } from '@/helpers/Validation'
import { reserveLanguage } from '@/language/helpers'
import { ReserveSubmitOptions, reserveSubmitHandler } from '@/state/Reserve'
import type { Session } from '@/types/Sessions'

import MobileFooterPortal from '../cart/MobileFooterPortal.vue'
import ReserveMobileFooter from '../cart/ReserveMobileFooter.vue'

/**
 * TODO Add support for admission passes & split this into smaller components.
 * @see https://tixtrack.atlassian.net/browse/TIC-1809
 */
@Component({
  name: 'ReserveQuantityFirst',
  components: {
    FormValidationGuard,
    AvailabilityStatus,
    SelectSession,
    MobileTotal,
    MobileCartButton,
    AdmitDetailsFields,
    FormInput2,
    SelectDate,
    SelectQuantities,
    Survey2,
    MobileFooterPortal,
    ReserveMobileFooter,
    RenewMembershipMessage,
    DynamicMessageGroup,
    EventDetailErrorMessages,
  },
})
export default class extends Vue {
  @Prop({ required: true })
  event: EventDetails

  @Prop()
  contexts: Set<string>

  readonly formId = 'reserve-quantity-first-form'

  quantitiesClosed: boolean = false
  selectedTicketTypes: TicketTypeQuantities = {}

  dates: CalendarApiResponse | null = null

  selectedDate: TixTime | null = null

  sessionClosed: boolean = false
  allSessions: Session[] | null = null
  selectedSession: Session | null = null

  surveyAnswers: Dict<Dict<Primitive>> = {}
  details: Dict<EventAdmitDetails[]> = {}

  submitting: boolean = false
  apiError: unknown = null
  fetchAvailabilityCalendarError: string | null = null
  sessionsAvailable = false
  loading = true

  l = reserveLanguage('reserveQuantityFirst')

  mounted() {
    fetchSessionsAvailability(this.event)
      .then((available) => {
        this.sessionsAvailable = available
      })
      .finally(() => {
        this.loading = false
      })
  }

  get quantitiesTotal() {
    return sumQuantities(this.selectedTicketTypes)
  }

  get totalAvailableDates(): number {
    const available = Object.values(this.dates!).filter((day) => day.status === 'available')
    return available.length
  }

  get availableSessions(): Session[] {
    return this.allSessions ? this.allSessions.filter((session) => !session.sold_out) : []
  }

  get selectedGroups(): LinkedTG[] {
    return this.event.ticketGroups.filter((group) => {
      return group.types.some((type) => this.selectedTicketTypes[type.id]?.quantity > 0)
    })
  }

  get surveySpecs(): SurveySpecWithTicketGroupID[] {
    return surveySpecsWithTicketGroupId(this.selectedGroups)
  }

  @Watch('selectedSession')
  onSessionChange(selectedSession: Session) {
    selectedSession ? this.closeSessions() : this.openSessions()
  }

  @Watch('selectedDate')
  onDateChange(selectedDate: Date) {
    selectedDate ? this.closeDate() : this.openDate()
  }

  @Watch('event')
  openQuantities() {
    this.quantitiesClosed = false
    this.resetSessions()
    this.resetDates()
  }

  async closeQuantities() {
    // Trigger a guard when there's no quantities selected
    if (this.quantitiesTotal === 0) {
      try {
        await this.validate()
      } catch (error) {
        // Error is handled by <FormValidationGuard>
        if (error instanceof ValidationError) return

        throw error
      }
    }

    this.quantitiesClosed = true
    this.apiError = null
    this.fetchAvailabilityCalendarError = null

    fetchCalendarForTicketTypes(this.event.id, this.selectedTicketTypesQuantities, this.showPrices.dates)
      .then((response) => {
        this.dates = response
      })
      .catch((err) => {
        this.quantitiesClosed = false
        const error = getApiErrorEntity(err)
        if (error?._code === 'validation_failed' && error?._context === 'event_calendar') {
          this.fetchAvailabilityCalendarError = 'You can only select up to 10 different types of tickets.'
        } else {
          this.fetchAvailabilityCalendarError = apiErrorMessageOrRethrow(err)
        }
      })
  }

  openDate() {
    // Deselect the date to expose the first 8 available dates.
    this.selectedDate = null
    this.resetSessions()
  }

  closeDate() {
    fetchSessionsAndPrices(this.event, this.selectedDate!, this.selectedTicketTypes).then((result) => {
      this.allSessions = sessionsWithPrices(result.sessions, result.priceSchedules)
    })
  }

  get showPrices(): ShowPricesConfig {
    const selectedTTs = this.event.ticketTypes.filter((type) => this.selectedTicketTypes[type.id]?.quantity > 0)
    return showPrices(this.event.config, selectedTTs)
  }

  openSessions() {
    this.sessionClosed = false
    this.selectedSession = null
  }

  closeSessions() {
    this.sessionClosed = true
  }

  resetSessions() {
    this.allSessions = null
    this.selectedSession = null
    this.sessionClosed = false
    this.apiError = null
  }

  resetDates() {
    this.dates = null
    this.selectedDate = null
    this.apiError = null
  }

  onSubmit() {
    this.submit()
  }

  submit(codes?: string[]): Promise<void> {
    const submitOptions: ReserveSubmitOptions = {
      ticketsPayload: reserveTicketsPayload(this.selectedTicketTypes, this.selectedSession, this.details, undefined),
      surveyAnswers: this.surveyAnswers,
      selectedGroups: this.selectedGroups,
      emitCallback: this.$emit.bind(this),
      codes: codes,
    }

    return this.validate()
      .then(() => {
        this.submitting = true
      })
      .then(() => reserveSubmitHandler(submitOptions))
      .catch((error) => {
        this.apiError = error
      })
      .finally(() => {
        this.submitting = false
      })
  }

  validate() {
    return reportFormValidity(this.$refs.form as HTMLFormElement)
  }

  get earliestDate(): TixTime {
    // Fall back on today (undefined).
    const firstDate = this.dates?.[0].date ?? undefined
    // TODO Use TixDate instead.
    return this.selectedDate ?? new TixTime(firstDate, this.event.venue.timezone)
  }

  get showCart() {
    return this.$store.getters['Cart/ticketCount'] > 0
  }

  get isChainedUpsell() {
    return isChainedUpsell(this.$route)
  }

  get selectedTicketTypesQuantities(): TicketTypeQuantities {
    const result: TicketTypeQuantities = {}
    for (const [ttId, quantityAndPrice] of Object.entries(this.selectedTicketTypes)) {
      if (quantityAndPrice.quantity > 0) {
        result[ttId] = quantityAndPrice
      }
    }
    return result
  }

  get sessionPickerTabsConfig() {
    return this.event.config.web?.session_picker_tabs
  }

  get dayData() {
    if (!this.dates) {
      return []
    }

    return processCalendarApiResponse(this.dates, this.event)
  }

  get capacityThreshold() {
    return capacityThreshold(this.dayData)
  }

  get selectedDateData() {
    return this.dayData?.find((d) => this.selectedDate?.isSame(d.date.format('DATE'), 'day'))
  }

  get showAvailability(): boolean {
    return showAvailability(this.capacityThreshold)
  }

  get selectQuantitiesTitle() {
    return this.$t('stepperGroupTitles.default', { type: 'tickets' })
  }

  get selectQuantitiesDescription() {
    return this.$t('stepperGroupDescriptions.default')
  }

  get submitButtonDisabled() {
    const showSelectDateLoader = this.quantitiesClosed && !this.dates
    const showSelectSessionLoader = this.quantitiesClosed && this.selectedDate && !this.allSessions
    return this.submitting || showSelectDateLoader || showSelectSessionLoader
  }

  get selectTicketsValidationMessage() {
    return `Please click the '${this.l.findAvailableTickets}' button to continue.`
  }
}
